import styled from 'styled-components';
import { CircularProgress, Typography } from '@mui/material';
import {
  Flex,
  FlexCenter,
  InfoTooltip,
  StatusChip,
} from '@elementary/common-ui/components';
import { capitalize } from '@elementary/common-ui/utils/common';
import { localizeDate } from '@elementary/common-ui/utils/datetime';
import PanelError from '../PanelError';
import { PipelineIncidentData } from './types';
import usePipelineRun from './hooks/usePipelineRun';
import { getToolDisplayName } from './utils';

const Grid = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 8px 16px;
  align-items: start;
`;

const Label = styled(Typography)`
  color: ${({ theme }) => theme.palette.common.gray4};
  font-size: 12px;
`;

const Value = styled(Typography)`
  font-size: 12px;
  word-break: break-word;
`;

type Props = {
  eventId: string;
  connectorData: PipelineIncidentData;
};

const PipelineRunInfo = ({ eventId, connectorData }: Props) => {
  const { data, isLoading, error } = usePipelineRun(eventId);

  const combinedConnectorData = {
    ...connectorData,
    ...data,
  };

  if (isLoading) {
    return (
      <FlexCenter sx={{ padding: '16px' }}>
        <CircularProgress size={24} />
      </FlexCenter>
    );
  }

  if (error) {
    return (
      <FlexCenter sx={{ padding: '16px' }}>
        <PanelError
          error={error as Error}
          errorName="ApiError"
          page="PipelineRunInfo"
        />
      </FlexCenter>
    );
  }

  return (
    <Grid>
      {combinedConnectorData.toolType && (
        <>
          <Label>Source</Label>
          <Value>{getToolDisplayName(combinedConnectorData.toolType)}</Value>
        </>
      )}

      {combinedConnectorData.connectorName && (
        <>
          <Label>Connector</Label>
          <Value>{combinedConnectorData.connectorName}</Value>
        </>
      )}

      {combinedConnectorData.connectorType && (
        <>
          <Label>Connector type</Label>
          <Value>{capitalize(combinedConnectorData.connectorType)}</Value>
        </>
      )}

      {combinedConnectorData.destination && (
        <>
          <Label>Destination</Label>
          <Value>{capitalize(combinedConnectorData.destination)}</Value>
        </>
      )}

      {data ? (
        <>
          <Label>Event</Label>
          <Value>{data.event}</Value>

          <Label>Status</Label>
          <Flex alignItems="center" gap={1}>
            <StatusChip status={data.status} size="small" />
            {data.rawStatus && <InfoTooltip title={data.rawStatus} size={14} />}
          </Flex>

          {data.failureReason && (
            <>
              <Label>Error</Label>
              <Value>{data.failureReason}</Value>
            </>
          )}

          <Label>Connector ID</Label>
          <Value>{combinedConnectorData.connectorId}</Value>

          <Label>Sync ID</Label>
          <Value>{data.syncId}</Value>

          {data.taskType && (
            <>
              <Label>Task type</Label>
              <Value>{data.taskType}</Value>
            </>
          )}

          {data.rescheduledAt && (
            <>
              <Label>Rescheduled at</Label>
              <Value>
                {localizeDate(data.rescheduledAt).format('MMM D, h:mm A')}
              </Value>
            </>
          )}

          <Label>Last occurred</Label>
          <Value>
            {localizeDate(data.eventTimestamp).format('MMM D, h:mm A')}
          </Value>
        </>
      ) : (
        <>
          <Label>Sync run</Label>
          <Value sx={{ color: ({ palette }) => palette.common.gray4 }}>
            Failed to load sync run information
          </Value>
        </>
      )}
    </Grid>
  );
};

export default PipelineRunInfo;
